export type ComplianceStreamFetchAction =
  | "skip_has_data"
  | "skip_in_flight"
  | "penalties_only"
  | "full_fetch";

export function decideComplianceStreamFetch(params: {
  selectedFacilityId: number;
  loadedFacilityId: number | null;
  inFlightFacilityId: number | null;
  hasComplianceData: boolean;
  hasPenaltiesLoaded: boolean;
}): ComplianceStreamFetchAction {
  const {
    selectedFacilityId,
    loadedFacilityId,
    inFlightFacilityId,
    hasComplianceData,
    hasPenaltiesLoaded,
  } = params;

  if (inFlightFacilityId === selectedFacilityId) {
    return "skip_in_flight";
  }

  if (loadedFacilityId === selectedFacilityId && hasComplianceData) {
    if (!hasPenaltiesLoaded) {
      return "penalties_only";
    }
    return "skip_has_data";
  }

  return "full_fetch";
}

export function shouldResetPenaltyExposureProgress(params: {
  fetchAction: ComplianceStreamFetchAction;
  progressSessionFacilityId: number | null;
  selectedFacilityId: number;
}): boolean {
  const { fetchAction, progressSessionFacilityId, selectedFacilityId } = params;
  if (fetchAction !== "full_fetch") {
    return false;
  }
  return progressSessionFacilityId !== selectedFacilityId;
}

export const MAX_EMPTY_PENALTIES_RETRIES = 2;

/** Skip starting a second penalties SSE when one is already running for this facility. */
export function shouldBeginPenaltiesFetch(params: {
  inFlightFacilityId: number | null;
  requestFacilityId: number;
}): boolean {
  return params.inFlightFacilityId !== params.requestFacilityId;
}

/** Only the latest penalties fetch may clear loading / in-flight markers. */
export function shouldFinalizePenaltiesFetch(params: {
  fetchGeneration: number;
  currentGeneration: number;
  requestFacilityId: number;
  currentFacilityId: number | null;
}): boolean {
  return (
    params.currentFacilityId === params.requestFacilityId &&
    params.currentGeneration === params.fetchGeneration
  );
}

/** After analysis SSE completes, kick penalties fetch unless one is already running. */
export function shouldFetchPenaltiesAfterAnalysis(params: {
  hasPenaltiesLoaded: boolean;
  cancelled: boolean;
  penaltiesFetchInFlightFacilityId: number | null;
  requestFacilityId: number;
}): boolean {
  return (
    !params.hasPenaltiesLoaded &&
    !params.cancelled &&
    params.penaltiesFetchInFlightFacilityId !== params.requestFacilityId
  );
}

export function shouldRetryEmptyPenaltiesResponse(params: {
  emptyResponsesSeen: number;
  maxEmptyResponses?: number;
}): boolean {
  const maxEmptyResponses =
    params.maxEmptyResponses ?? MAX_EMPTY_PENALTIES_RETRIES;
  return params.emptyResponsesSeen <= maxEmptyResponses;
}

/** Dashboard can start compliance SSE while profile auto-save runs in the background. */
export function shouldWaitForProfileBeforeCompliance(params: {
  activeTab: string;
  canViewFacilityProfileTab: boolean;
  isProfileApproved: boolean;
}): boolean {
  if (params.activeTab === "dashboard") {
    return false;
  }
  return params.canViewFacilityProfileTab && !params.isProfileApproved;
}

/** Delay first profile-values POST until compliance SSE emits init (or timeout). */
export const PROFILE_AUTO_SAVE_DEFER_TIMEOUT_MS = 3000;

export function shouldDeferProfileAutoSave(params: {
  enabled: boolean;
  complianceStreamInitSignal: number;
  deferTimedOut: boolean;
}): boolean {
  if (!params.enabled) {
    return false;
  }
  if (params.complianceStreamInitSignal > 0) {
    return false;
  }
  return !params.deferTimedOut;
}

/** Seed the penalty checklist as soon as the dashboard tab is shown for a facility. */
export function shouldSeedPenaltyExposureProgressOnDashboard(params: {
  activeTab: string;
  facilityId: number | null | undefined;
  postPurchaseResolving: boolean;
  progressSessionFacilityId: number | null;
}): boolean {
  return (
    params.activeTab === "dashboard" &&
    params.facilityId != null &&
    !params.postPurchaseResolving &&
    params.progressSessionFacilityId !== params.facilityId
  );
}

/** Do not abort in-flight compliance when profile bootstrap finishes during dashboard load. */
export function shouldResetComplianceAfterProfileSave(params: {
  acceptedFacilityId: number;
  complianceInFlightFacilityId: number | null;
}): boolean {
  return params.complianceInFlightFacilityId !== params.acceptedFacilityId;
}

export function shouldAbortComplianceStreamsOnEffectCleanup(params: {
  effectFacilityId: number | null;
  currentFacilityId: number | null;
  effectFetchGeneration: number;
  currentFetchGeneration: number;
}): boolean {
  const {
    effectFacilityId,
    currentFacilityId,
    effectFetchGeneration,
    currentFetchGeneration,
  } = params;

  if (effectFacilityId !== currentFacilityId) {
    return true;
  }
  if (effectFetchGeneration !== currentFetchGeneration) {
    return true;
  }
  return false;
}

export type PendingPenaltiesMergeEntry<TResponse = unknown> = {
  facilityId: number;
  response: TResponse;
};

/** Read pending penalties finished before analysis; clears the slot when consumed. */
export function takePendingPenaltiesMerge<TResponse extends {
  sections: { penaltiesAndTotalExposure: unknown };
}>(
  getCurrent: () => PendingPenaltiesMergeEntry<TResponse> | null,
  clear: () => void,
  requestFacilityId: number,
): TResponse | null {
  const pending = getCurrent();
  if (
    !pending ||
    pending.facilityId !== requestFacilityId ||
    !pending.response.sections.penaltiesAndTotalExposure
  ) {
    return null;
  }
  clear();
  return pending.response;
}

/**
 * Facility permissions must be (re)fetched whenever the permission check does not
 * match the selected facility. Reselecting the same facility clears
 * permissionCheckedFacilityId without changing facility_id; keying the fetch on
 * facility_id alone left the compliance load waiting forever ("No penalty data").
 */
export function shouldFetchFacilityPermissions(params: {
  selectedFacilityId: number | null | undefined;
  checkedFacilityId: number | null;
  permissionsLoading: boolean;
}): boolean {
  if (params.selectedFacilityId == null) return true;
  if (params.permissionsLoading) return false;
  return params.checkedFacilityId !== params.selectedFacilityId;
}
